import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LOGIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) {
      const to = loc.state?.from || "/";
      navigate(to, { replace: true });
    }
  }, [user, loc.state, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
      toast.error("Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] grid-paper flex items-center justify-center px-4">
      <div className="w-full max-w-[420px]">
        <div className="mb-8">
          <div className="flex flex-col items-center gap-0 mb-6">
            <img src="/brand/prephacker-logo.webp" alt="PrepHacker" className="h-56 w-auto object-contain" />
            <span className="text-base font-bold uppercase tracking-[0.42em] text-slate-700 -mt-6">Connect</span>
          </div>
          <p className="text-sm text-slate-600 text-center max-w-[340px] mx-auto">
            The internal command centre for converting trial users into paid subscribers.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm space-y-4"
        >
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Sign In
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5">
              <Mail className="h-3.5 w-3.5" /> Email
            </label>
            <input
              data-testid={LOGIN.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
              placeholder="you@prephacker.com"
              required
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5">
              <Lock className="h-3.5 w-3.5" /> Password
            </label>
            <div className="relative">
              <input
                data-testid={LOGIN.password}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                data-testid="login-toggle-password-visibility"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-400 hover:text-slate-700 transition-colors duration-150"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {err && (
            <div data-testid={LOGIN.error} className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {err}
            </div>
          )}

          <button
            data-testid={LOGIN.submit}
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-slate-800 transition-colors duration-150 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
            <ArrowRight className="h-4 w-4" />
          </button>

          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3 leading-relaxed">
            Forgot your password? Ask an administrator to reset it via
            {" "}
            <code className="text-[10px] px-1 py-0.5 bg-slate-100 rounded font-mono">backend/deploy/reset-password.py</code>
            {" "}on the server. Credentials are never displayed here.
          </div>
        </form>
      </div>
    </div>
  );
}
